import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Instagram } from "lucide-react";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: "Flownavy is a motion-design ad studio that creates short-form video ads for direct-to-consumer and B2B SaaS brands. This policy explains what information we collect through this website and how we use it.",
  },
  {
    h: "2. Information we collect",
    p: "When you submit a brief to claim your free sample, we collect the details you provide: your name, brand or company name, work email, hero product and URL, any asset links, target platform, brand vibe, preferred video length, and your key message. We do not ask for payment card details on this site.",
  },
  {
    h: "3. How we use your information",
    p: "We use your brief solely to prepare and deliver your free sample, to communicate with you about your project, and to follow up if you choose to proceed. We do not sell your information, and we do not use it for advertising to third parties.",
  },
  {
    h: "4. Email delivery",
    p: "Brief submissions are delivered to us by email through our email provider (Resend). Your details are transmitted securely and used only to fulfil your request.",
  },
  {
    h: "5. Data retention",
    p: "We keep brief submissions for as long as needed to deliver your sample and manage any resulting project, after which they may be archived or deleted. You can ask us to delete your data at any time.",
  },
  {
    h: "6. Cookies & analytics",
    p: "This site uses only the essential storage required to function. If we add analytics in future, we will update this policy and, where required, ask for your consent.",
  },
  {
    h: "7. Your rights",
    p: "You may request access to, correction of, or deletion of the personal information you have shared with us. To make a request, email us using the address below and we will respond promptly.",
  },
  {
    h: "8. Contact",
    p: "Questions about this policy or your data? Reach out and we'll help.",
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="relative min-h-screen bg-[#050505] text-white">
      <div className="pointer-events-none absolute inset-0 hairline-grid opacity-40" />

      <div className="relative mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-24">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 ff-mono text-xs uppercase tracking-widest text-zinc-400 transition-colors hover:text-[#C7FF3C]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Flownavy
        </Link>

        <p className="mt-12 ff-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
          Legal · Effective June 2026
        </p>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-none tracking-tight sm:text-6xl">
          Privacy Policy
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400">
          Your trust matters. This page explains, in plain language, what we collect when you use
          this site and how we handle it.
        </p>

        <div className="mt-14 space-y-12 border-t border-zinc-900 pt-12">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`privacy-section-${s.h[0]}`}>
              <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{s.h}</h2>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base">{s.p}</p>
            </section>
          ))}

          <section className="flex flex-col gap-3 rounded-2xl border border-zinc-800 bg-[#0d0d0d] p-6">
            <a
              href="mailto:saurav@flownavy.com"
              className="inline-flex items-center gap-2 ff-mono text-sm text-zinc-300 transition-colors hover:text-white"
            >
              <Mail className="h-4 w-4" /> saurav@flownavy.com
            </a>
            <a
              href="https://www.instagram.com/flow_navy?igsh=aHpkNXFsanI0bTJi"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 ff-mono text-sm text-zinc-300 transition-colors hover:text-[#C7FF3C]"
            >
              <Instagram className="h-4 w-4" /> @flow_navy
            </a>
          </section>
        </div>

        <div className="mt-16 border-t border-zinc-900 pt-8">
          <Link
            to="/"
            className="font-display text-lg font-extrabold tracking-tight text-white transition-colors hover:text-[#C7FF3C]"
          >
            FLOWNAVY<span className="text-zinc-500">.</span>
          </Link>
          <p className="mt-2 ff-mono text-xs text-zinc-600">
            © {new Date().getFullYear()} Flownavy Motion Studio
          </p>
        </div>
      </div>
    </main>
  );
}
